/**
 * Planetary cell grids — integer addressing for region-cell worldgen.
 *
 * Each cube face carries an n×n integer grid per level. n derives from the
 * planet radius alone, so every process agrees with zero communication.
 * Face-local cube coords u,v ∈ [-1,1]; cell (x,y) spans
 * [-1 + 2x/n, -1 + 2(x+1)/n] × likewise in v. Small moons degrade to 1×1.
 */
import { FACES, faceDir } from "../cubesphere.js";

export const LEVEL_PROVINCE = 0;
export const LEVEL_REGION = 1;

export interface CellId {
  face: number;
  x: number;
  y: number;
  level: number;
}

const REGION_TARGET_M = 2000; // ~2 km arc per region cell (spec §3.2)

/** Region cells along one face edge: clamp(round((R·π/2) / 2km), 1, 64). */
export function regionsPerFace(radius: number): number {
  return Math.min(64, Math.max(1, Math.round((radius * Math.PI * 0.5) / REGION_TARGET_M)));
}

/**
 * Cells along one face edge at `level`. Province count is regionsPerFace >> 2.
 * WARNING: when regionsPerFace is not a multiple of 4, province cells do NOT
 * nest region cells exactly (the >>2 truncates the boundary). A region→province
 * mapping must therefore go through `dirToCell(radius, LEVEL_PROVINCE, dir)` on a
 * direction (e.g. the region center) — never index arithmetic like `rx >> 2`.
 */
export function cellsPerFace(radius: number, level: number): number {
  const r = regionsPerFace(radius);
  return level === LEVEL_REGION ? r : Math.max(1, r >> 2);
}

/** Approximate arc length of one cell (meters) — for sizing steps/probes. */
export function cellArcM(radius: number, level: number): number {
  return (radius * Math.PI * 0.5) / cellsPerFace(radius, level);
}

/** Center of a cell as a unit sphere direction. */
export function cellCenterDir(radius: number, cell: CellId, out: number[] | Float64Array): void {
  const n = cellsPerFace(radius, cell.level);
  const u = -1 + (2 * (cell.x + 0.5)) / n;
  const v = -1 + (2 * (cell.y + 0.5)) / n;
  faceDir(cell.face, u, v, out);
}

/** Classify a unit direction into a cell: dominant axis → face, gnomonic u,v. */
export function dirToCell(radius: number, level: number, dx: number, dy: number, dz: number): CellId {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const az = Math.abs(dz);
  let face: number;
  if (ax >= ay && ax >= az) face = dx >= 0 ? 0 : 1;
  else if (ay >= ax && ay >= az) face = dy >= 0 ? 2 : 3;
  else face = dz >= 0 ? 4 : 5;
  const f = FACES[face];
  const w = dx * f.normal[0] + dy * f.normal[1] + dz * f.normal[2];
  const u = (dx * f.right[0] + dy * f.right[1] + dz * f.right[2]) / w;
  const v = (dx * f.up[0] + dy * f.up[1] + dz * f.up[2]) / w;
  const n = cellsPerFace(radius, level);
  const x = Math.min(n - 1, Math.max(0, Math.floor(((u + 1) * 0.5) * n)));
  const y = Math.min(n - 1, Math.max(0, Math.floor(((v + 1) * 0.5) * n)));
  return { face, x, y, level };
}

/** Unique uint32 per cell: 3b face | 2b level | 12b x | 12b y (n ≤ 64 fits). */
export function packCell(cell: CellId): number {
  return (((cell.face << 26) | (cell.level << 24) | (cell.x << 12) | cell.y) >>> 0);
}

/** Inverse of {@link packCell}. Kept here so every address consumer decodes
 * the binding cell format identically. */
export function unpackCell(packed: number): CellId {
  const p = packed >>> 0;
  return {
    face: (p >>> 26) & 0x7,
    level: (p >>> 24) & 0x3,
    x: (p >>> 12) & 0xfff,
    y: p & 0xfff,
  };
}
